import type { SubscriberResponse } from '@cvo/domain/api/models/bff/Subscriber';

/**
 * Keycloak composable that handles authentication state and token management.
 * Automatically injects tokens into authenticated API clients when login/refresh occurs.
 */
export function useAuth() {
  const { $keycloak: keycloak } = useNuxtApp();
  const api = useApi();
  // Reactive state for authentication
  const isAuthenticated = useState('authenticated', () => keycloak.authenticated ?? false);

  /**
   * Login with Keycloak and automatically inject tokens
   */
  async function login(): Promise<void> {
    try {
      await keycloak.login({
        redirectUri: window.location.origin + window.location.pathname, // Doesn't include the hash
      });
    } catch (error) {
      console.error('Login failed:', error);
      useO11y().reportError(error, { context: 'login' });
      throw error;
    }
  }

  /**
   * Logout and clear tokens from API clients
   */
  async function logout(): Promise<void> {
    const runtimeConfig = useRuntimeConfig();
    const redirectUri = runtimeConfig.public.logout?.redirectUri || window.origin;
    try {
      await keycloak.logout({ redirectUri });
      // TODO Later: Redirect to login page
    } catch (error) {
      console.error('Logout failed:', error);
      useO11y().reportError(error, { context: 'logout' });
      throw error;
    }
  }

  async function getUser(): Promise<SubscriberResponse | null> {
    try {
      const isAccountCreated = keycloak.tokenParsed?.person_id;
      if (isAuthenticated.value && isAccountCreated) {
        return await api.subscriber.getUserInfo().then((res) => res.data);
      }
      return null;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      useO11y().reportError(error, { context: 'get_user' });
      throw error;
    }
  }

  /**
   * If user is fresh and starting the registration for first time then
   * getUser() will return 400 because of no subscriber id. This getUserFromToken
   * function is exposed to retrieve the email and other details in future,
   * if needed from JWT stored by keycloak.
   * @returns
   */
function getUserFromToken() {
  if (!keycloak.tokenParsed) return null;
  return {
    email: keycloak.tokenParsed.email,
    emailVerified: keycloak.tokenParsed.email_verified,
    familyName: keycloak.tokenParsed.family_name,
    name: keycloak.tokenParsed.name,
    birthdate: keycloak.tokenParsed.birthdate,
    gender: keycloak.tokenParsed.gender,
    givenName: keycloak.tokenParsed.given_name,
    preferredUsername: keycloak.tokenParsed.preferred_username,
    locale: keycloak.tokenParsed.locale,
    sub: keycloak.tokenParsed.sub,
  };
}

  /**
   * Force refresh the authentication token to get the latest claims from Keycloak.
   * Useful after registration or profile updates to ensure the token contains updated user data.
   */
  function refreshToken() {
    console.debug('Token expired, refreshing...');
    // @ts-expect-error onTokenExpired type does not accept parameters but it actually does
    return keycloak.onTokenExpired?.(-1);
  }

  return {
    // Authentication methods
    login,
    logout,
    getUser,
    getUserFromToken,
    refreshToken,

    // Reactive properties
    isAuthenticated: readonly(isAuthenticated),
  };
}
